/**
 * @module
 * @format
 */

import {COMPILE, UPGRADE} from '@acng/frontend-stargazer';
import {guard, OBJECT, RECORD, STRING} from '@acng/frontend-rubicon';
import {IS_CHANGEABLE, IS_INPUT, IS_SELECT, IS_TEXTAREA} from '@acng/frontend-rubicon';
import {parseInteger} from '@acng/frontend-bounty/std/number.js';
import {CLICK, on} from '@acng/frontend-bounty/dom/event.js';
import {ChangeEventController} from 'nova';
/** @import {UserProfile} from './profile.js' */
import {cdn} from '../../core/service/cdn.js';
import {t} from '../../locale/config/translate.js';
import {cmToIn, inToCm, kgToLbs, lbsToKg} from '../../locale/service/units.js';
import {user} from '../user.js';
import {getOptions, VALID_FILLABLE} from '../model/user-profile.js';

class ProfileChange extends ChangeEventController {
  /**
   * @readonly
   */
  #service;

  /**
   * @param {UserProfile} profileService
   */
  constructor(profileService) {
    super();
    this.#service = profileService;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} input
   * @returns {Promise<void>}
   */
  async change(input) {
    let {name: key, value} = input;

    switch (key) {
      case 'height':
      case 'penislength':
      case 'penisperimeter':
        if (!user.usesMetricUnits()) {
          value = inToCm(parseInteger(value)).toString();
        }

        break;
      case 'weight':
        if (!user.usesMetricUnits()) {
          value = lbsToKg(parseInteger(value)).toString();
        }

        break;
    }

    try {
      await this.#service.put({[key]: value}, input);
    } catch (e) {
      input.value = input.getAttribute('value') ?? '';

      return;
    }

    switch (key) {
      case 'gender':
        this.#service.male = value == 'm';
        this.#service.female = value == 'f';
    }
  }

  /**
   * @param {Element} element
   */
  async [COMPILE](element) {
    await this.#service.get();

    ASSERT: guard(element, IS_CHANGEABLE);
    const {name} = element;

    let lengthUnit = 'inch';
    let weightUnit = 'lbs';

    if (user.usesMetricUnits()) {
      lengthUnit = 'cm';
      weightUnit = 'kg';
    }

    switch (name) {
      case 'description':
        ASSERT: guard(element, IS_TEXTAREA);
        element.autocomplete = 'off';
        element.maxLength = 4096;
        break;
      case 'birthday':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'off';
        element.type = 'date';
        element.min = ProfileChange.#makeDate(120);
        element.max = ProfileChange.#makeDate(18);
        break;
      case 'zip':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'postal-code';
        element.type = 'text';
        break;
      case 'city':
        ASSERT: guard(element, IS_INPUT);
        element.autocomplete = 'address-level2';
        element.type = 'text';
        break;
      case 'country': {
        ASSERT: guard(element, IS_SELECT);
        element.autocomplete = name;
        const initialOption = new Option(await t('core.pleaseChoose'), '', true, true);
        initialOption.disabled = true;
        element.options.add(initialOption);

        const data = await cdn(`/language/${user.lang}/countries`);
        ASSERT: guard(data, OBJECT({countries: OBJECT({iso: RECORD(STRING, STRING)})}));

        for (const option of Object.entries(data.countries.iso)
          .sort((a, b) => new Intl.Collator(user.lang).compare(a[1], b[1]))
          .map(([value, text]) => new Option(text, value))) {
          element.options.add(option);
        }

        break;
      }
      case 'height':
      case 'penislength':
      case 'penisperimeter': {
        ASSERT: guard(element, IS_INPUT);
        element.type = 'number';
        element.placeholder = await t('core.inPortalLengthUnit', {lengthUnit});

        break;
      }
      case 'weight': {
        ASSERT: guard(element, IS_INPUT);
        element.type = 'number';
        element.placeholder = await t('core.inPortalWeightUnit', {weightUnit});

        break;
      }
      case 'gender':
      case 'ethnic':
      case 'eyecolor':
      case 'haircolor':
      case 'hairlength':
      case 'body':
      case 'bodyhair':
      case 'pubichair':
      case 'brasize':
      case 'glasses':
      case 'tattoos':
      case 'piercings':
      case 'marital':
      case 'smoking': {
        ASSERT: guard(element, IS_SELECT);
        element.autocomplete = 'off';
        const initialOption = new Option(await t('core.pleaseChoose'), '', true, true);
        initialOption.disabled = true;
        element.options.add(initialOption);

        for (const {value, text} of await getOptions(user.lang)(name)) {
          element.options.add(new Option(text, value));
        }

        break;
      }
    }
  }

  /**
   * @param {Element} element
   */
  [UPGRADE](element) {
    ASSERT: {
      guard(element, IS_CHANGEABLE);
      const {localName, name} = element;
      guard(element.name, VALID_FILLABLE, `<${localName} name="${name}">`);
    }

    const {name} = element;
    let value = `${this.#service.data[name] ?? ''}`;

    if (!user.usesMetricUnits()) {
      switch (name) {
        case 'height':
        case 'penislength':
        case 'penisperimeter':
          value = `${cmToIn(parseInt(value))}`;
          break;
        case 'weight':
          value = `${kgToLbs(parseInt(value))}`;
          break;
      }
    }

    element.value = value;

    if (name == 'birthday' && 'showPicker' in element) {
      element.style.pointerEvents = 'none';
      on(element, CLICK, () => element.showPicker());
    }
  }

  /**
   * @param {number} yearsAgo
   * @returns {string}
   */
  static #makeDate(yearsAgo) {
    const date = new Date();
    date.setFullYear(date.getFullYear() - yearsAgo);

    return date.toISOString().substring(0, 10);
  }
}

export {ProfileChange};
